import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Loader2, Pause, Play} from 'lucide-react';
import {useAuthenticatedMedia} from '../../hooks/useAuthenticatedMedia';
import type {MatrixEncryptedFile} from '../../utils/encryptedMedia';

interface VoiceMessagePlayerProps {
    src: string;
    /** Seeds the waveform so the same message always draws the same bars. */
    seed: string;
    encryptedFile?: MatrixEncryptedFile;
    mimeType?: string;
    /** `info.duration` in ms, shown before the audio has loaded. */
    durationMs?: number;
    isOwn: boolean;
    /** Transcript row and anything else under the player. */
    children?: React.ReactNode;
}

const BAR_COUNT = 28;
const SPEEDS = [1, 1.5, 2] as const;

export const formatVoiceTime = (seconds: number): string => {
    const s = Math.max(0, Math.round(seconds));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Deterministic bar heights (6–28px) from a string, so a message keeps its shape. */
export const waveformBars = (seed: string, count = BAR_COUNT): number[] => {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) h = Math.imul(h ^ seed.charCodeAt(i), 16777619);
    return Array.from({length: count}, () => {
        h = Math.imul(h ^ (h >>> 13), 1274126177);
        return 6 + (Math.abs(h) % 23);
    });
};

/**
 * A voice message (D-CHAT-08/S04): play / pause, a waveform that fills as it
 * plays, the time, and a 1× / 1.5× / 2× speed toggle once it has started.
 */
export const VoiceMessagePlayer: React.FC<VoiceMessagePlayerProps> = ({src, seed, encryptedFile, mimeType, durationMs, isOwn, children}) => {
    const {blobUrl, isLoading, error, retry} = useAuthenticatedMedia(src, encryptedFile, mimeType);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [playing, setPlaying] = useState(false);
    const [started, setStarted] = useState(false);
    const [current, setCurrent] = useState(0);
    const [duration, setDuration] = useState((durationMs ?? 0) / 1000);
    const [speed, setSpeed] = useState<(typeof SPEEDS)[number]>(1);
    const bars = useMemo(() => waveformBars(seed), [seed]);

    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = speed;
    }, [speed]);

    const toggle = async () => {
        const audio = audioRef.current;
        if (!audio) return;
        if (audio.paused) {
            setStarted(true);
            await audio.play().catch(() => setPlaying(false));
        } else {
            audio.pause();
        }
    };

    const progress = duration > 0 ? current / duration : 0;
    const shownTime = started ? current : duration;

    return (
        <div data-testid="chat.message.voice" className={`flex w-[300px] max-w-full flex-col gap-1.5 rounded-2xl py-2 pl-2 pr-3 ${isOwn ? 'bg-brand-tint' : 'bg-surface-muted'}`}>
            <div role="group" aria-label={`Voice message ${formatVoiceTime(duration)}`} className="flex items-center gap-2.5">
                <button type="button" onClick={() => { void toggle(); }}
                        disabled={!blobUrl}
                        aria-label={playing ? 'Pause voice message' : 'Play voice message'}
                        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-brand text-white hover:bg-brand-hover disabled:bg-brand-disabled">
                    {isLoading
                        ? <Loader2 size={16} aria-hidden="true" className="!p-0 animate-spin !text-white"/>
                        : playing
                            ? <Pause size={16} aria-hidden="true" className="!p-0 !text-white" fill="currentColor"/>
                            : <Play size={16} aria-hidden="true" className="!p-0 !text-white" fill="currentColor"/>}
                </button>
                <span aria-hidden="true" className="flex h-7 min-w-0 flex-1 items-center gap-0.5 overflow-hidden">
                    {bars.map((height, i) => (
                        <span key={i} style={{height}}
                              className={`w-[3px] shrink-0 rounded-[1px] ${i / bars.length < progress ? 'bg-brand' : isOwn ? 'bg-[#A99CF0]' : 'bg-ink-disabled'}`}/>
                    ))}
                </span>
                <span className={`min-w-[30px] shrink-0 text-right text-[13px] tabular-nums ${started ? 'text-ink' : 'text-ink-muted'}`}>
                    {formatVoiceTime(shownTime)}
                </span>
                {started && (
                    <button type="button" aria-label={`Playback speed ${speed}x`}
                            onClick={() => setSpeed(SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length])}
                            className="h-7 min-w-9 shrink-0 rounded-lg bg-brand/[0.14] px-1.5 text-[13px] font-semibold text-ink">
                        {speed}×
                    </button>
                )}
            </div>
            {error && (
                <div className="flex items-center gap-2 text-xs text-ink-muted">
                    Couldn’t load the audio.
                    <button type="button" onClick={retry} className="font-semibold text-brand-ink underline">Try again</button>
                </div>
            )}
            {blobUrl && (
                <audio ref={audioRef} src={blobUrl} preload="metadata" className="hidden"
                       onLoadedMetadata={e => { if (Number.isFinite(e.currentTarget.duration)) setDuration(e.currentTarget.duration); }}
                       onTimeUpdate={e => setCurrent(e.currentTarget.currentTime)}
                       onPlay={() => setPlaying(true)}
                       onPause={() => setPlaying(false)}
                       onEnded={() => { setPlaying(false); setCurrent(0); setStarted(false); }}/>
            )}
            {children}
        </div>
    );
};

export default VoiceMessagePlayer;
